import type { OfferDraftRowKind } from '../../../types';
import { contributesToOfferNetTotal, lineTotalCents } from './offerEventualPosition';
import { clampTitleLevel, MIN_TITLE_LEVEL } from './offerTitleHierarchy';

export type TitleSubtotal = {
  afterRowId: string;
  titleRowId: string;
  numberLabel: string;
  titleName: string;
  net: number;
  titleLevel: number;
};

export function titleSubtotalLabel(sub: Pick<TitleSubtotal, 'numberLabel' | 'titleName'>): string {
  const num = String(sub.numberLabel ?? '').trim();
  const name = String(sub.titleName ?? '').trim();
  if (num && name) return `Summe ${num} ${name}`;
  if (num) return `Summe ${num}`;
  if (name) return `Summe ${name}`;
  return 'Summe';
}

export function groupTitleSubtotalsByAfterRowId(subs: TitleSubtotal[]): Map<string, TitleSubtotal[]> {
  const grouped = new Map<string, TitleSubtotal[]>();
  for (const sub of subs) {
    const list = grouped.get(sub.afterRowId);
    if (list) list.push(sub);
    else grouped.set(sub.afterRowId, [sub]);
  }
  return grouped;
}

export function computeTitleSubtotals(
  rows: Array<{
    id: string;
    kind: OfferDraftRowKind;
    isEventual?: boolean | null;
    quantity: number;
    unit_price_vk: number;
    numberLabel?: string;
    item_name?: string;
    titleLevel?: number | null;
  }>,
): TitleSubtotal[] {
  type OpenTitle = {
    id: string;
    numberLabel: string;
    titleName: string;
    titleLevel: number;
    lastRowId: string;
    net: number;
    pricedCount: number;
  };
  const out: TitleSubtotal[] = [];
  const stack: OpenTitle[] = [];

  const flushFrom = (minLevel: number) => {
    while (stack.length > 0 && stack[stack.length - 1]!.titleLevel >= minLevel) {
      const title = stack.pop()!;
      if (title.lastRowId && title.pricedCount > 0) {
        out.push({
          afterRowId: title.lastRowId,
          titleRowId: title.id,
          numberLabel: title.numberLabel,
          titleName: title.titleName,
          net: Math.round(title.net * 100) / 100,
          titleLevel: title.titleLevel,
        });
      }
    }
  };

  for (const row of rows) {
    if (row.kind === 'title') {
      const level = clampTitleLevel(row.titleLevel ?? MIN_TITLE_LEVEL);
      flushFrom(level);
      stack.push({
        id: row.id,
        numberLabel: String(row.numberLabel ?? '').trim(),
        titleName: String(row.item_name ?? '').trim(),
        titleLevel: level,
        lastRowId: row.id,
        net: 0,
        pricedCount: 0,
      });
      continue;
    }
    if (stack.length === 0) continue;
    for (const title of stack) {
      title.lastRowId = row.id;
      if (contributesToOfferNetTotal(row)) {
        title.net += lineTotalCents(row.quantity, row.unit_price_vk);
        title.pricedCount += 1;
      }
    }
  }
  flushFrom(MIN_TITLE_LEVEL);
  return out;
}

/**
 * Customer-facing title summary: keep every title row and only priced rows
 * that are not assigned to a title. Child positions stay available internally
 * and for structured e-invoice exports.
 */
export function filterRowsForTitleSummary<
  T extends {
    kind: OfferDraftRowKind;
  },
>(rows: T[]): T[] {
  let activeTitle = false;
  return rows.filter((row) => {
    if (row.kind === 'title') {
      activeTitle = true;
      return true;
    }
    if (activeTitle) return false;
    return row.kind !== 'text' && row.kind !== 'image';
  });
}
